import { PostSection } from "@/components/blog/PostSection";
import { PostHighlight } from "@/components/blog/PostHighlight";
import { PostList } from "@/components/blog/PostList";
import { PostQuote } from "@/components/blog/PostQuote";

export function GeoPositioningSection() {
  return (
    <PostSection id="geo-positioning" title="Posicionamiento GEO: El Nuevo Paradigma del SEO Local">
      <p className="mb-6">
        En 2025, el <strong>posicionamiento GEO (geográfico)</strong> emerge como una evolución crítica del SEO local tradicional. 
        Mientras el SEO Local se centraba en optimizar Google My Business, el GEO abarca un ecosistema más amplio y tecnológicamente avanzado.
      </p>

      <PostHighlight>
        <strong>Diferencia clave:</strong> El SEO Local tradicional optimiza para búsquedas con ciudad ("restaurante Madrid"). 
        El Posicionamiento GEO te posiciona para búsquedas sin ciudad basadas en ubicación en tiempo real ("restaurante cerca de mí", "dónde comer ahora").
      </PostHighlight>

      <h3 className="text-2xl font-semibold mt-8 mb-4 text-white">¿Por qué el GEO es crítico en 2025?</h3>
      
      <p className="font-semibold mb-3">Tres cambios tecnológicos que hacen del GEO una necesidad:</p>
      <PostList
        items={[
          "<strong>Atlas de ChatGPT:</strong> El nuevo buscador de OpenAI prioriza geolocalización en tiempo real. No necesita que el usuario especifique ciudad. Ya indexa más de 200M de negocios y crece 10% mensual.",
          "<strong>Google SGE (Search Generative Experience):</strong> La búsqueda con IA de Google entiende intención geográfica implícita. El 68% de búsquedas móviles ya tienen intención local sin mencionar ubicación.",
          "<strong>Explosión de búsquedas 'cerca de mí':</strong> Crecimiento del 900% desde 2020. Los usuarios ya no escriben ciudades, esperan que la IA detecte su ubicación automáticamente."
        ]}
      />

      <h3 className="text-2xl font-semibold mt-8 mb-4 text-white">SEO Local vs Posicionamiento GEO</h3>

      <div className="overflow-x-auto my-6">
        <table className="w-full border-collapse bg-seo-darkBlue/30 rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gradient-to-r from-seo-blue to-seo-cyan">
              <th className="p-4 text-left text-white">Aspecto</th>
              <th className="p-4 text-left text-white">SEO Local Tradicional</th>
              <th className="p-4 text-left text-white">Posicionamiento GEO</th>
            </tr>
          </thead>
          <tbody className="text-gray-200">
            <tr className="border-b border-seo-blue/20">
              <td className="p-4 font-semibold">Búsquedas objetivo</td>
              <td className="p-4">"Fontanero Madrid"</td>
              <td className="p-4">"Fontanero cerca de mí"</td>
            </tr>
            <tr className="border-b border-seo-blue/20 bg-seo-darkBlue/20">
              <td className="p-4 font-semibold">Plataformas</td>
              <td className="p-4">Google Maps, GMB</td>
              <td className="p-4">Atlas, Google SGE, Perplexity, GMB</td>
            </tr>
            <tr className="border-b border-seo-blue/20">
              <td className="p-4 font-semibold">Tecnología</td>
              <td className="p-4">Keywords + ubicación manual</td>
              <td className="p-4">Geolocalización en tiempo real + IA</td>
            </tr>
            <tr className="border-b border-seo-blue/20 bg-seo-darkBlue/20">
              <td className="p-4 font-semibold">Radio de acción</td>
              <td className="p-4">Ciudad completa</td>
              <td className="p-4">Hiperlocal (50m-500m)</td>
            </tr>
            <tr className="bg-seo-darkBlue/20">
              <td className="p-4 font-semibold">Relevancia 2025</td>
              <td className="p-4">⚠️ Esencial pero insuficiente</td>
              <td className="p-4">✅ Crítico y diferenciador</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 className="text-2xl font-semibold mt-8 mb-4 text-white">Elementos clave del Posicionamiento GEO</h3>

      <p className="font-semibold mb-3">Para optimizar tu posicionamiento GEO necesitas:</p>
      <PostList
        items={[
          "<strong>Schema GeoShape:</strong> Marcado estructurado que define tu área de servicio con coordenadas geográficas precisas.",
          "<strong>Meta tags geográficos:</strong> geo.region, geo.placename, geo.position, ICBM coordinates para que los motores de búsqueda entiendan tu ubicación exacta.",
          "<strong>Contenido conversacional:</strong> Páginas optimizadas para búsquedas en lenguaje natural ('dónde contratar', 'quién hace', 'cerca de aquí').",
          "<strong>Optimización hiperlocal:</strong> Contenido específico por zonas de 50-500m de radio, no solo por ciudad.",
          "<strong>Coordenadas precisas:</strong> Latitud y longitud exactas en todos los marcados de datos estructurados."
        ]}
      />

      <PostQuote
        text="En 2025, un negocio con solo SEO Local tradicional pierde el 60-70% del tráfico de búsquedas locales. Los usuarios ya no buscan 'en Madrid', buscan 'cerca de mí', y los nuevos buscadores con IA premian el posicionamiento GEO."
      />

      <h3 className="text-2xl font-semibold mt-8 mb-4 text-white">La estrategia ganadora: SEO Local + GEO</h3>

      <p className="mb-4">
        No es una cuestión de elegir entre SEO Local o GEO, sino de <strong>combinar ambas estrategias</strong> en dos capas:
      </p>

      <div className="grid md:grid-cols-2 gap-6 my-6">
        <div className="bg-seo-darkBlue/50 border border-seo-blue/30 rounded-lg p-6">
          <h4 className="text-xl font-bold text-white mb-3">Capa 1: SEO Local Base</h4>
          <ul className="space-y-2 text-gray-200">
            <li>✓ Ficha GMB optimizada</li>
            <li>✓ Gestión de reseñas</li>
            <li>✓ Categorías y atributos</li>
            <li>✓ Publicaciones regulares</li>
          </ul>
        </div>
        <div className="bg-gradient-to-br from-seo-bright/20 to-seo-cyan/20 border-2 border-seo-bright/50 rounded-lg p-6">
          <h4 className="text-xl font-bold text-white mb-3">Capa 2: Posicionamiento GEO</h4>
          <ul className="space-y-2 text-gray-200">
            <li>✓ Schema GeoShape</li>
            <li>✓ Contenido conversacional</li>
            <li>✓ Meta tags geográficos</li>
            <li>✓ Optimización hiperlocal</li>
          </ul>
        </div>
      </div>

      <p className="mb-4">
        Con nuestra tecnología de IA, automatizamos más de 80 optimizaciones GEO que manualmente tardarían meses, 
        logrando resultados visibles en <strong>2-4 semanas</strong> en lugar de 6 meses del SEO Local tradicional.
      </p>

      <PostHighlight>
        <strong>Resultado:</strong> Cobertura total de búsquedas locales. Capturas búsquedas con ciudad, conversacionales, y de proximidad. 
        Tu negocio visible en todas las plataformas: Google Maps, Atlas, Google SGE, Perplexity.
      </PostHighlight>
    </PostSection>
  );
}
